import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Flex,
  Text,
  VStack,
  useColorModeValue,
  HStack,
  Image,
  useToast,
} from "@chakra-ui/react";
import Header from "../components/Header";
import PaymentModal from "../components/Payment/PaymentModal";
import { pxToRem } from "../utils/editor";
import { PaymentService } from "../services/paymentService";
import {
  CurrentPlanResponse,
  StripePaymentRequest,
  PayPalPaymentRequest,
  StartFreeTrialResponse,
} from "../types/Payment";
import { useSearchParams, useNavigate } from "react-router-dom";
import { pricingPlans } from "../utils/const";
import { capitalizeFirstLetter } from "../utils/const.ts";
import CancelSubscriptionModal from "../components/Payment/CancelSubscriptionModal";
import { useTranslation } from "react-i18next";

// 新增接口定义
interface SelectedPlan {
  planType: string;
  period: string;
  price: string;
}

const currentpricingPlans = [
  ...pricingPlans,
  {
    planType: "free-trial",
    period: "Trial",
    price: "0",
  },
];

const PaymentCurrentPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation(); // 添加国际化hook

  // 新增选中套餐状态
  const [selectedPlan, setSelectedPlan] = useState<SelectedPlan | null>(null);

  const bgColor = useColorModeValue("#ffffff", "gray.800");
  const textColor = useColorModeValue("#333333", "white");
  const mutedTextColor = "#8E9ABC";
  const blueColor = "#1D8AFF";
  const redColor = "#e53e3e";
  const toast = useToast();

  // 添加状态控制弹窗的显示和隐藏
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<
    string | null
  >(null);
  // 添加取消订阅弹窗状态
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [selectedCancelReasons, setSelectedCancelReasons] = useState<string[]>(
    []
  );
  // 点击next
  const [step, setStep] = useState<number>(1);

  // 新增状态
  const [currentPlan, setCurrentPlan] = useState<CurrentPlanResponse | null>(
    null
  );

  const [isLoading, setIsLoading] = useState(false);

  // 新增免费试用状态
  const [freeTrialData, setFreeTrialData] =
    useState<StartFreeTrialResponse | null>(null);
  console.log(freeTrialData);
  // 获取当前计划信息 - 无需修改API调用，但需要处理新的响应字段
  const fetchCurrentPlan = async () => {
    try {
      const planData = await PaymentService.getCurrentPlan();
      const foundPlan = currentpricingPlans.find(
        (plan) => plan.planType === planData?.plan_type
      );
      planData.period = foundPlan?.period || "none";
      setCurrentPlan(planData);
    } catch (error) {
      console.error("获取当前计划失败:", error);
      const errorMsg = error?.response?.data?.detail;
      toast({
        title: "Error",
        description: errorMsg || "Failed to fetch current plan",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // 新增免费试用函数
  const startFreeTrial = async () => {
    setIsLoading(true);
    try {
      const result = await PaymentService.startFreeTrial();
      setFreeTrialData(result);

      // if (!result?.detail) {
      toast({
        title: "Free Trial Started",
        description: result?.message || "Your free trial has been activated",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      // 重新获取当前计划信息
      await fetchCurrentPlan();
      // }
    } catch (error) {
      console.error("启动免费试用失败:", error);
      toast({
        title: "Free Trial Error",
        description: "Failed to start free trial. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // 页面加载时获取数据
  useEffect(() => {
    fetchCurrentPlan();
  }, []);

  // 打开支付弹窗的函数
  const openPaymentModal = () => setIsPaymentModalOpen(true);

  // 关闭支付弹窗的函数
  const closePaymentModal = () => setIsPaymentModalOpen(false);

  // 打开取消订阅弹窗的函数
  const openCancelModal = async () => {
    setIsCancelModalOpen(true);
  };

  // 关闭取消订阅弹窗的函数
  const closeCancelModal = () => {
    setIsCancelModalOpen(false);
    setSelectedCancelReasons([]);
    setStep(1);
  };

  // 选择支付方式的函数
  const selectPaymentMethod = (method: string) => {
    setSelectedPaymentMethod(method);
  };

  // 选择取消原因的函数
  const toggleCancelReason = (reasonId: string) => {
    if (selectedCancelReasons.includes(reasonId)) {
      setSelectedCancelReasons(
        selectedCancelReasons.filter((id) => id !== reasonId)
      );
    } else {
      setSelectedCancelReasons([...selectedCancelReasons, reasonId]);
    }
  };

  // 继续支付流程的函数 - 根据新API更新
  // 新增URL参数解析逻辑
  useEffect(() => {
    const planType = searchParams.get("planType");

    if (planType) {
      // 根据planType从pricingPlans中查找对应的套餐信息
      const foundPlan = currentpricingPlans.find(
        (plan) => plan.planType === planType
      );
      if (foundPlan) {
        setSelectedPlan({
          planType: foundPlan.planType,
          period: foundPlan.period,
          price: foundPlan.price,
        });
      }
    } else {
      // 如果没有参数，检查是否有当前订阅的plan
      if (currentPlan?.plan_type && currentPlan.plan_type !== "none") {
        // 如果用户已经订阅，显示当前订阅的plan
        const foundPlan = currentpricingPlans.find(
          (plan) => plan.planType === currentPlan.plan_type
        );

        if (foundPlan) {
          setSelectedPlan({
            planType: foundPlan.planType,
            period: foundPlan.period,
            price: foundPlan.price,
          });
        }
      } else {
        // 如果没有订阅，显示默认套餐且可以点击支付
        setSelectedPlan(currentpricingPlans[0]);
      }
    }
  }, [searchParams, navigate, currentPlan]);

  // 修改continuePayment函数，使用动态的planType
  const continuePayment = async () => {
    if (!selectedPaymentMethod || !selectedPlan) return;

    setIsLoading(true);
    try {
      // 根据新API，只需要传递plan_type
      const paymentData = {
        plan_type: selectedPlan.planType, // 使用动态的planType
      };

      if (selectedPaymentMethod === "credit") {
        // 使用Stripe支付
        const stripeData: StripePaymentRequest = paymentData;
        const result = await PaymentService.payByStripe(stripeData);

        // 根据新API响应，使用approval_url字段
        if (result.approval_url) {
          window.location.href = result.approval_url;
        } else if (result.session_id) {
          // 备用方案，如果还是返回session_id
          window.location.href = `https://checkout.stripe.com/pay/${result.session_id}`;
        }
      } else if (selectedPaymentMethod === "paypal") {
        // 使用PayPal支付
        const paypalData: PayPalPaymentRequest = paymentData;
        const result = await PaymentService.payByPayPal(paypalData);

        // 跳转到PayPal支付页面
        if (result.approval_url) {
          window.location.href = result.approval_url;
        }
      }

      closePaymentModal();
      toast({
        title: "Success",
        description: "Redirecting to payment page...",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("支付失败:", error);
      const msg = error?.response?.data?.error;

      toast({
        title: "Payment Error",
        description: msg || "Failed to process payment. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      bg={bgColor}
      h="100%"
      w="100%"
      overflow={"auto"}
      padding={pxToRem(30)}
      pt={0}
    >
      <Box minW="1200px">
        {/* Header */}
        <Header />
        <Box
          fontSize={pxToRem(24)}
          color="#333"
          cursor="pointer"
          mb={pxToRem(20)}
          ml={pxToRem(20)}
          onClick={() => navigate("/payment")}
          display={"flex"}
        >
          <Image
            transform={`translateY(${pxToRem(2)})`}
            src="/static/payment/arrow-left.svg"
            w={pxToRem(24)}
          />
          Back to select other plans
        </Box>
        {/* Main Content */}
        <Box px={{ base: pxToRem(4), md: pxToRem(8) }} py={pxToRem(8)} pb={0}>
          <Flex maxW={pxToRem(1400)} mx="auto">
            {/* Left Side - Feature Card */}
            <Box
              flex={`0 0 ${pxToRem(522)}`}
              bg="url('/static/payment-bg.png') no-repeat"
              borderRadius={pxToRem(24)}
              // h={"78vh"}
              p={pxToRem(48)}
              marginRight={pxToRem(80)}
              position="relative"
              overflow="hidden"
              boxShadow={`0 ${pxToRem(4)} ${pxToRem(20)} rgba(0,0,0,0.08)`}
            >
              <VStack
                align="start"
                spacing={pxToRem(6)}
                position="relative"
                zIndex={1}
              >
                <VStack align="start" spacing={pxToRem(2)}>
                  <Box fontSize={pxToRem(24)} color={textColor}>
                    Unlock your full writing potential with
                  </Box>
                  <Box
                    marginTop={pxToRem(10)}
                    fontSize={pxToRem(36)}
                    fontWeight="bold"
                    color={textColor}
                  >
                    Professional Plan.
                  </Box>
                </VStack>

                <VStack
                  mt={pxToRem(20)}
                  align="start"
                  fontSize={pxToRem(20)}
                  color={textColor}
                >
                  <Box>
                    Streamline Your Writing: Let AI handle structure,
                    formatting, and rewriting, so you can focus on your ideas.
                  </Box>

                  <Box marginTop={pxToRem(10)}>
                    Elevate Your Content: Expert-level suggestions and reviews
                    for polished, professional, and logical writing at any
                    level.
                  </Box>

                  <Box marginTop={pxToRem(10)}>
                    Effortless Publishing: Save time with one-click outputs
                    tailored to leading journal and conference templates.
                  </Box>
                </VStack>
              </VStack>
            </Box>

            {/* Right Side - Plans and Current Plan */}
            <Box
              flex={"1"}
              h="75vh"
              display={"flex"}
              flexDirection={"column"}
              justifyContent={"center"}
            >
              <Box fontSize={pxToRem(36)} color={textColor}>
                Start Now
                <Box>
                  Transform your writing with our &nbsp;<b>Professional Plan</b>
                </Box>
              </Box>

              <Flex
                mt={pxToRem(24)}
                gap={{ base: pxToRem(6), md: pxToRem(8) }}
                pr={pxToRem(34)}
                direction={{ base: "column", lg: "row" }}
              >
                {/* Professional Plan Card - 动态显示选中的套餐 */}
                <Box
                  flex={`0 0 ${pxToRem(436)}`}
                  h={pxToRem(522)}
                  bg="#E0EAFF"
                  border="1px solid"
                  borderColor="#1D8AFF"
                  borderRadius={pxToRem(20)}
                  mr={pxToRem(80)}
                  p={{ base: pxToRem(5), md: pxToRem(6) }}
                  boxShadow={`0 ${pxToRem(2)} ${pxToRem(12)} rgba(0,0,0,0.06)`}
                >
                  <VStack
                    spacing={pxToRem(4)}
                    align="stretch"
                    justifyContent={"center"}
                  >
                    <Text
                      textAlign={"center"}
                      fontSize={pxToRem(50)}
                      fontWeight="bold"
                      color={textColor}
                      mb={0}
                      mt={pxToRem(20)}
                    >
                      {selectedPlan?.planType === "free-trial"
                        ? "Free Trial"
                        :  selectedPlan?.period}
                    </Text>
                    <Flex
                      align="center"
                      justifyContent={"center"}
                    >
                      <Text
                        fontSize={pxToRem(64)}
                        fontWeight="bold"
                        color={blueColor}
                        mb={0}
                      >
                        ${selectedPlan?.price}
                      </Text>
                      {/* <Text fontSize={pxToRem(24)} color={textColor}>
                        /{selectedPlan?.period?.toLowerCase()}
                      </Text> */}
                    </Flex>
                    {/* Feature list */}
                    <VStack
                      h={pxToRem(210)}
                      align="start"
                      justifyContent={'center'}
                      spacing={pxToRem(12)}
                      px={pxToRem(38)}
                    >
                      {selectedPlan &&
                        pricingPlans
                          .find(
                            (plan) => plan.planType === selectedPlan.planType
                          )
                          ?.desc.map((feature, index) => (
                            <Flex key={index} align="center" gap={pxToRem(18)}>
                              <Box
                                w={pxToRem(6)}
                                h={pxToRem(6)}
                                bg={blueColor}
                                borderRadius="50%"
                                flexShrink={0}
                              />
                              <Text
                                fontSize={pxToRem(14)}
                                color={textColor}
                                lineHeight="1.2"
                                mb={0}
                              >
                                {t(feature)}
                              </Text>
                            </Flex>
                          ))}
                    </VStack>

                    <Button
                      bg={"#333"}
                      color="white"
                      borderRadius={pxToRem(20)}
                      w={pxToRem(251)}
                      margin={"0 auto"}
                      mt={pxToRem(20)}
                      h={pxToRem(53)}
                      fontSize={pxToRem(24)}
                      fontWeight="700"
                      _hover={{
                        bg: "#2c5aa0",
                        transform: `translateY(${pxToRem(-1)})`,
                      }}
                      transition="all 0.2s"
                      onClick={
                        selectedPlan?.planType === "free-trial"
                          ? startFreeTrial
                          : openPaymentModal
                      }
                      isLoading={isLoading}
                      display={
                        !searchParams.get("planType") &&
                        currentPlan?.plan_type &&
                        currentPlan.plan_type !== "none"
                          ? "none"
                          : "block"
                      }
                    >
                      {selectedPlan?.planType === "free-trial"
                        ? "Start Free Trial"
                        : "Purchase Now"}
                    </Button>
                  </VStack>
                </Box>

                {/* Current Plan Card */}
                {currentPlan?.plan_type && currentPlan.plan_type !== "none" &&  (currentPlan?.days_left > 0) && (
                  <Box flex="1">
                    <Box
                      bg="#F5F6FA"
                      border="1px solid"
                      borderColor="#F5F6FA"
                      borderRadius={pxToRem(20)}
                      boxShadow={`0 ${pxToRem(2)} ${pxToRem(
                        12
                      )} rgba(0,0,0,0.06)`}
                      transition="all 0.2s"
                      _hover={{
                        boxShadow: `0 ${pxToRem(4)} ${pxToRem(
                          20
                        )} rgba(0,0,0,0.1)`,
                      }}
                      p={pxToRem(32)}
                      h={pxToRem(522)}
                    >
                      <VStack spacing={pxToRem(6)} align="stretch">
                        <Text
                          fontSize={pxToRem(32)}
                          fontWeight="bold"
                          color={textColor}
                        >
                          Current Plan
                        </Text>

                        {/* 当前计划显示 - 修正字段名和新增时间显示 */}
                     
                        <HStack align="center" spacing={pxToRem(2)}>
                          <Text
                            fontSize={pxToRem(24)}
                            fontWeight="bold"
                            mb={0}
                            color={textColor}
                            mr={pxToRem(26)}
                            w={pxToRem(200)}
                          >
                            {currentPlan?.plan_type === "free-trial"
                              ? "Free Plan"
                              : "Professional Plan"}
                          </Text>
                          <Box
                            bg={"#16B560"}
                            color="white"
                            px={pxToRem(24)}
                            py={pxToRem(4)}
                            borderRadius={pxToRem(20)}
                            fontSize={pxToRem(16)}
                          >
                            {currentPlan?.days_left} days left
                          </Box>
                        </HStack>

                        {currentPlan?.plan_type === "free-trial" ? (
                          <Box
                            bg="url(/static/payment/free-bg.png)"
                            backgroundSize={"100% 100%"}
                            backgroundRepeat={"no-repeat"}
                            borderRadius={pxToRem(20)}
                            p={pxToRem(20)}
                            mt={pxToRem(50)}
                            w={"100%"}
                            minH={pxToRem(204)}
                          >
                            <Text
                              fontSize={pxToRem(32)}
                              fontWeight="700"
                              mb={pxToRem(16)}
                            >
                              Doenba Edit Professional
                            </Text>

                            <Text
                              fontSize={pxToRem(24)}
                              color={"#333"}
                              mb={pxToRem(16)}
                            >
                              Get full access to all Doenba Edit features.
                            </Text>
                            <Box
                              display="inline-block"
                              bg="rgba(255, 255, 255, 0.35)"
                              borderRadius={pxToRem(20)}
                              px={pxToRem(24)}
                              py={pxToRem(2)}
                              mb={pxToRem(16)}
                              border={"1px solid #FFF"}
                            >
                              <Text
                                fontSize={pxToRem(16)}
                                fontWeight="600"
                                color="#333"
                                mb={0}
                              >
                                7-day free trial
                              </Text>
                            </Box>
                            <Box
                              textAlign={"center"}
                              mt={pxToRem(20)}
                              mb={pxToRem(16)}
                            >
                              <Button
                                bg={"#fff"}
                                color="#1D8AFF"
                                borderRadius={pxToRem(20)}
                                fontSize={pxToRem(16)}
                                px={pxToRem(24)}
                                py={pxToRem(8)}
                                _hover={{
                                  bg: "#fff",
                                  transform: `translateY(${pxToRem(-1)})`,
                                }}
                                transition="all 0.2s"
                                onClick={() => navigate("/edit")}
                              >
                                Try Now
                              </Button>
                            </Box>
                          </Box>
                        ) : (
                          <VStack
                            spacing={pxToRem(4)}
                            align="stretch"
                            mb={pxToRem(140)}
                            mt={pxToRem(40)}
                          >
                            <Flex align="center" gap={pxToRem(55)}>
                              <Text
                                w={pxToRem(170)}
                                fontSize={pxToRem(16)}
                                color={mutedTextColor}
                              >
                                Next Payment Date
                              </Text>
                              <Text
                                fontSize="sm"
                                fontWeight="semibold"
                                color={textColor}
                              >
                                {currentPlan?.expiry_timestamp
                                  ? new Date(
                                      currentPlan.expiry_timestamp
                                    ).toLocaleDateString("en-US", {
                                      year: "numeric",
                                      month: "long",
                                      day: "numeric",
                                    })
                                  : ""}
                              </Text>
                            </Flex>

                            <Flex align="center" gap={pxToRem(55)}>
                              <Text
                                w={pxToRem(170)}
                                fontSize={pxToRem(16)}
                                color={mutedTextColor}
                              >
                                Payment Method
                              </Text>
                              <Text
                                fontSize={pxToRem(16)}
                                fontWeight="semibold"
                                color={textColor}
                              >
                                {capitalizeFirstLetter(
                                  currentPlan?.payment_platform
                                )}
                              </Text>
                              {/* <Button
                        variant="outline"
                        size="sm"
                        borderRadius={pxToRem(20)}
                        fontSize={pxToRem(16)}
                        borderColor="#e2e8f0"
                        color={textColor}
                      >
                        Change Payment Method
                      </Button> */}
                            </Flex>
                          </VStack>
                        )}

                        {currentPlan?.plan_type !== "free-trial" && (
                          <Button
                            bg={redColor}
                            color="white"
                            borderRadius={pxToRem(20)}
                            w={pxToRem(189)}
                            h={pxToRem(43)}
                            margin={"0 auto"}
                            _hover={{
                              bg: "#c53030",
                              transform: `translateY(${pxToRem(-1)})`,
                            }}
                            transition="all 0.2s"
                            onClick={openCancelModal}
                            isLoading={isLoading}
                          >
                            Cancel Subscription
                          </Button>
                        )}
                      </VStack>
                    </Box>
                  </Box>
                )}
              </Flex>
              <Box
                textDecoration="underline"
                cursor="pointer"
                onClick={() => navigate("/refund-policy")}
                mt={"20px"}
                ml={"20px"}
              >
                Refund & Cancellation Policy
              </Box>
            </Box>
          </Flex>
        </Box>
        {/* 支付方式选择弹窗 */}
        <PaymentModal
          isOpen={isPaymentModalOpen}
          onClose={closePaymentModal}
          selectedPaymentMethod={selectedPaymentMethod}
          onSelectPaymentMethod={selectPaymentMethod}
          onContinuePayment={continuePayment}
          isLoading={isLoading}
        />
        {/* 取消订阅弹窗 */}
        <CancelSubscriptionModal
          isOpen={isCancelModalOpen}
          onClose={closeCancelModal}
          step={step}
          setStep={setStep}
          selectedCancelReasons={selectedCancelReasons}
          onToggleCancelReason={toggleCancelReason}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
        />
      </Box>
    </Box>
  );
};

export default PaymentCurrentPage;
